import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { MuiButton, MuiChip, MuiDrawer } from '@ace/ace-ui-web';
import PerfOrderDetailsSkeletonDataGrid from '../../../../../components/skeletons/performance/ecommerce/PerfOrderDetailsSkeletonDataGrid';
import pagesStandardStyles from '../../../../Pages.module.css';
import orderDetailsStyles from './OrderDetails.module.css';
import { getDataGridStyles } from '../../../../GridStyles';
import {
  formatNumberWithCommas,
  isMobileDevice,
  formatLoadDate,
  isValidApiDate,
  getAlternatingRowClassName,
} from '../../../../../utils/appUtils';
import lang from '../../../../../languages/en.json';
import { useAppSelector } from '../../../../../redux/hooks';
import { useClickHandler } from '../../../../../utils/usageMetricsLogger';
import {
  selectEcomOrderDetails,
  selectEcomOrderDetailsLoading,
} from '../../../../../redux/PerformanceReducer/Ecommerce/ecomOrderDetailsSlice';
import { useBuildDynamicUrl } from '../../../../../hooks/useDynamicUrl';
import CANCELLED from '../../../../view-info-content/performance/CANCELLED';
import FULFILLED from '../../../../view-info-content/performance/FULFILLED';
import type { EcomOrderDetails } from '../../../../../models/EcomOrderDetailsModel';

const COLUMN_MIN_WIDTH = 115.2;
const PAGE_NM = 'Performance';
const TAB_NM = 'Ecommerce';
const SECTION_NM = 'Order Details';

// ─── Props interface ─────────────────────────────────────────────────────────

interface OrderDetailsProps {
  /** Pre-fetched data for print view — skips Redux read and API fetch when provided */
  data?: EcomOrderDetails | null;
  /** When true, hides interactive elements (chips, buttons, drawer) */
  isPrintView?: boolean;
}

// Row interface for DataGrid
interface GridRow {
  id: number;
  status: string;
  statusCode: 'CANCELLED' | 'FULFILLED';
  quantity: number | null;
}

// Render status chip
const renderStatusChip = (params: GridRenderCellParams<GridRow, string>) => {
  const status = params.value as string;
  const color = params.row.statusCode === 'CANCELLED' ? 'default' : 'green';

  return (
    <MuiChip
      colors={color}
      label={status}
      size="small"
      variant="filled"
    />
  );
};

// Render quantity with formatting
const renderQuantity = (params: GridRenderCellParams<GridRow, number | null>) => {
  if (params.value === null || params.value === undefined) {
    return <span className={pagesStandardStyles.centeredPlaceholder}>-</span>;
  }
  return formatNumberWithCommas(params.value.toString());
};

const OrderDetails: React.FC<OrderDetailsProps> = ({ data: dataProp, isPrintView = false }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [drawerContent, setDrawerContent] = useState<{
    title: string;
    content: React.ReactNode;
  }>({
    title: '',
    content: null,
  });
  const [isMobile, setIsMobile] = useState<boolean>(isPrintView ? false : isMobileDevice());

  // Data fetch handled by Header
  const handleAndLogClick = useClickHandler(PAGE_NM, TAB_NM);
  const buildDynamicUrl = useBuildDynamicUrl();
  const cancelledUrl = buildDynamicUrl('WFC', 'AHOC');
  const closeoutUrl = buildDynamicUrl('WFC', 'AHOF');

  // Select data and loading state from Redux
  const reduxData = useAppSelector(selectEcomOrderDetails);
  const reduxLoading = useAppSelector(selectEcomOrderDetailsLoading);

  // Use prop data (print view) or Redux data (live view)
  const orderDetailsData = dataProp ?? reduxData;
  const isLoading = isPrintView ? false : reduxLoading;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Create rows from live data
  const rowData = useMemo(
    () =>
      orderDetailsData
        ? [
            {
              id: 1,
              status: lang.orderDetailsContent.statusCanceled,
              statusCode: 'CANCELLED' as const,
              quantity: orderDetailsData.canceledQuantity,
            },
            {
              id: 2,
              status: lang.orderDetailsContent.statusFulfilled,
              statusCode: 'FULFILLED' as const,
              quantity: orderDetailsData.fulfilledQuantity,
            },
          ]
        : [],
    [orderDetailsData],
  );

  // Render Take Action button (inside component to access dynamic URLs and click tracking)
  const renderTakeActionButton = useCallback(
    (params: GridRenderCellParams<GridRow>): JSX.Element => {
      const statusCode = params.row.statusCode;
      const buttonText =
        statusCode === 'CANCELLED'
          ? lang.orderDetailsContent.canceledOrders
          : lang.orderDetailsContent.completeOrders;
      const url = statusCode === 'CANCELLED' ? cancelledUrl : closeoutUrl;
      const clickId =
        statusCode === 'CANCELLED'
          ? `${SECTION_NM} - Take Action - Canceled Orders`
          : `${SECTION_NM} - Take Action - Complete Orders`;

      return (
        <MuiButton
          colors="secondary"
          iconPosition="right"
          iconType="launch"
          size="small"
          onClick={() => {
            if (url) {
              handleAndLogClick(clickId, {
                url,
                urlTarget: '_blank',
              });
            }
          }}>
          {buttonText}
        </MuiButton>
      );
    },
    [cancelledUrl, closeoutUrl, handleAndLogClick],
  );

  // Render View Info link (must stay inside component - uses state setters)
  const renderViewInfoLink = useCallback(
    (params: GridRenderCellParams<GridRow>): JSX.Element => {
      const statusCode = params.row.statusCode;

      return (
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            let ContentComponent: React.FC;
            let title: string;
            let clickId: string;

            if (statusCode === 'CANCELLED') {
              ContentComponent = CANCELLED;
              title = CANCELLED.title || 'Cancelled Orders';
              clickId = `${SECTION_NM} - View Info - Cancelled Orders`;
            } else {
              ContentComponent = FULFILLED;
              title = FULFILLED.title || 'Fulfilled Orders';
              clickId = `${SECTION_NM} - View Info - Fulfilled Orders`;
            }

            handleAndLogClick(clickId, {
              callback: () => {
                setDrawerContent({
                  title,
                  content: <ContentComponent />,
                });
                setIsDrawerOpen(true);
              },
            });
          }}
          className={pagesStandardStyles.viewInfoLink}>
          {lang.pageContent.viewInfo}
        </a>
      );
    },
    [handleAndLogClick, setDrawerContent, setIsDrawerOpen],
  );

  // Define columns — print view only shows status + quantity
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: lang.orderDetailsContent.columnStatus,
        minWidth: COLUMN_MIN_WIDTH,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        cellClassName: pagesStandardStyles.stickyColumn,
        renderCell: isPrintView ? undefined : renderStatusChip,
      },
      ...(!isPrintView
        ? [
            {
              field: 'moreInfo',
              headerName: lang.orderDetailsContent.columnMoreInfo,
              minWidth: COLUMN_MIN_WIDTH,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: 'left' as const,
              renderCell: renderViewInfoLink,
            },
          ]
        : []),
      ...(!isMobile && !isPrintView
        ? ([
            {
              field: 'takeAction',
              headerName: lang.pageContent.columnTakeAction,
              minWidth: COLUMN_MIN_WIDTH,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: 'left',
              renderCell: renderTakeActionButton,
            },
          ] as GridColDef[])
        : []),
      {
        field: 'quantity',
        headerName: lang.orderDetailsContent.columnQuantity,
        minWidth: COLUMN_MIN_WIDTH,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'right',
        headerAlign: 'right',
        renderCell: renderQuantity,
      },
    ],
    [isMobile, isPrintView, renderViewInfoLink, renderTakeActionButton],
  );

  if (isPrintView && !orderDetailsData) {
    return null;
  }

  return (
    <div>
      {/* Page Header */}
      <div className={pagesStandardStyles.pageHeader}>
        <div className={pagesStandardStyles.headerContainer}>
          <div>
            <h2 className={pagesStandardStyles.pageTitle}>{lang.orderDetailsContent.title}</h2>
            <p className={pagesStandardStyles.pageSubtitle}>
              {lang.orderDetailsContent.subtitlePrefix} {orderDetailsData?.updateTime ?? ''}{' '}
              {lang.orderDetailsContent.subtitleToday} {lang.orderDetailsContent.subtitleMiddle}{' '}
              {isValidApiDate(orderDetailsData?.asOfDate)
                ? formatLoadDate(orderDetailsData.asOfDate)
                : 'N/A'}{' '}
              {lang.orderDetailsContent.subtitleSuffix}
            </p>
          </div>
        </div>
      </div>

      {/* DataGrid */}
      <div className={orderDetailsStyles.orderDetailsGrid}>
        {isLoading ? (
          <PerfOrderDetailsSkeletonDataGrid isMobile={isMobile} />
        ) : !orderDetailsData ? (
          <div className={pagesStandardStyles.errorContent}>
            {lang.pageContent.dataUnavailableError.replace(
              '{section}',
              lang.orderDetailsContent.title,
            )}
          </div>
        ) : (
          <DataGrid
            rows={rowData}
            columns={columns}
            disableRowSelectionOnClick
            disableVirtualization
            disableColumnMenu
            disableColumnSorting
            disableColumnResize
            hideFooter
            autoHeight
            className={pagesStandardStyles.dataGrid}
            getRowClassName={params =>
              getAlternatingRowClassName(
                params,
                pagesStandardStyles.evenRow,
                pagesStandardStyles.oddRow,
              )
            }
            columnHeaderHeight={52}
            columnBufferPx={1500}
            sx={{
              ...getDataGridStyles('status'),
            }}
          />
        )}
      </div>

      {/* MuiDrawer — hidden in print view */}
      {!isPrintView && (
        <MuiDrawer
          anchor="right"
          headerText={drawerContent.title}
          onClose={() => setIsDrawerOpen(false)}
          open={isDrawerOpen}>
          {drawerContent.content}
        </MuiDrawer>
      )}
    </div>
  );
};

export default OrderDetails;
